import { useEffect, useState, useCallback } from 'react';
import { notifySessionExpired } from '../lib/methods/callApi';
import { devLog, devWarn, devError } from '../utils/devLogger';
import auctionSocketService from '../services/auctionSocketService';
import { useUserAuth } from '../context/UserAuthContext';
import { useAdminAuth } from '../context/AdminAuthContext';
import {
  ResponseModel,
  AuctionDetailView,
  BidPlacedData,
  BidHistoryItem
} from '../types/socket.types';

// 안전한 useAdminAuth 래퍼
const useSafeAdminAuth = () => {
  try {
    return useAdminAuth();
  } catch (error) {
    // AdminAuthProvider가 없는 경우 (사용자 페이지)
    return {
      accessToken: null,
      isLoggedIn: false,
      ready: false,
      login: () => {},
      logout: () => {}
    };
  }
};

interface UseAuctionSocketProps {
  auctionIndex?: number;
  autoConnect?: boolean;
  autoRequestData?: boolean; // 소켓 연결 시 자동으로 데이터 요청할지 여부
  useAdminToken?: boolean; // CMS에서 사용할 때는 true로 설정
}

interface UseAuctionSocketReturn {
  // 연결 상태
  isConnected: boolean;
  
  // 경매 데이터
  auctionData: AuctionDetailView | null;
  
  // 입찰 내역
  bidHistory: BidHistoryItem[];
  
  // 카운트다운 상태
  countdownStatus: {
    isRunning: boolean;
    isPaused: boolean;
    remainingTime: number;
    lotIndex?: number;
  };
  
  // 현재 진행 중인 lot 상태 (소켓 이벤트로 관리)
  currentLot: {
    lotIndex: number;
    lotNo: number;
    lotName?: string;
  } | null;
  
  // 경매 종료 상태
  isAuctionEnded: boolean;
  
  // 사용자 차단 상태
  isBanned: boolean;
  
  // 에러 상태
  error: string | null;
  
  // 액션 함수들
  connectSocket: () => void;
  disconnectSocket: () => void;
  getAuctionInfo: (auctionIndex: number) => void;
  joinAuction: (auctionIndex: number) => void;
  placeBid: (auctionIndex: number, lotIndex: number, price: number) => void;
  getBidHistory: (auctionIndex: number, lotIndex: number) => void;
  
  // 관리자 전용 기능들
  startBid: (auctionIndex: number, lotNo: number) => void;
  cmsPlaceBid: (auctionIndex: number, lotIndex: number, price: number, code: string) => void;
  banUser: (auctionIndex: number, userIndex: number) => void;
  unbanUser: (auctionIndex: number, userIndex: number) => void;
  getBanUserList: (auctionIndex: number) => void;
  nextLot: (auctionIndex: number) => void;
  
  // 타이머 제어 (새로운 기능)
  timerControl: (auctionIndex: number, lotIndex: number, action: 'start' | 'pause' | 'resume' | 'reset' | 'stop', duration?: number) => void;
  getTimerStatus: (auctionIndex: number, lotIndex?: number) => void;
  
  // 기존 호환성을 위한 간단한 카운트다운
  auctionCountdown: () => void;
  
  // 상태 리셋
  clearError: () => void;
  clearBidHistory: () => void;
}

export function useAuctionSocket({
  auctionIndex,
  autoConnect = true,
  autoRequestData = true, // 기본값은 true (기존 동작 유지)
  useAdminToken = false // CMS에서 사용할 때는 true로 설정
}: UseAuctionSocketProps = {}): UseAuctionSocketReturn {
  
  const { accessToken: userAccessToken } = useUserAuth();
  const { accessToken: adminAccessToken } = useSafeAdminAuth();
  
  // 사용할 토큰 결정 (로그인 상태와 관계없이 게스트 연결 지원)
  const accessToken = useAdminToken ? adminAccessToken : userAccessToken;
  const [isConnected, setIsConnected] = useState(false);
  const [auctionData, setAuctionData] = useState<AuctionDetailView | null>(null);
  const [bidHistory, setBidHistory] = useState<BidHistoryItem[]>([]);
  const [countdownStatus, setCountdownStatus] = useState({
    isRunning: false,
    isPaused: false,
    remainingTime: 0,
    lotIndex: undefined as number | undefined
  });
  const [currentLot, setCurrentLot] = useState<{
    lotIndex: number;
    lotNo: number;
    lotName?: string;
  } | null>(null);
  const [isAuctionEnded, setIsAuctionEnded] = useState(false);
  const [isBanned, setIsBanned] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toSafeNumber = useCallback((value: any, fallback: number) => {
    const asNumber = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(asNumber) ? asNumber : fallback;
  }, []);

  const toOptionalSafeNumber = useCallback((value: any): number | undefined => {
    const asNumber = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(asNumber) ? asNumber : undefined;
  }, []);

  // 디버그 모드 감지 (개발 환경 또는 localhost)
  const isDebugMode = process.env.NODE_ENV === 'development' || 
                     window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';

  // 로깅 함수 - 디버그 모드에서만 실행
  const debugLog = useCallback((message: string, data?: any) => {
    if (isDebugMode) {
      devLog(message, data);
    }
  }, [isDebugMode]);

  debugLog('🚧 [useAuctionSocket] 디버그 모드:', isDebugMode);

  // 소켓 이벤트 핸들러들 - 최적화된 버전
  const handleConnectionChange = useCallback((connected: boolean) => {
    debugLog('🔌 [useAuctionSocket] 연결 상태 변경:', connected);
    setIsConnected(connected);
    
    // autoRequestData가 true이고 auctionIndex가 있을 때만 자동 요청
    if (connected && auctionIndex && autoRequestData) {
      debugLog('🏛️ 연결 후 자동 액션 실행 - auctionIndex:', auctionIndex);
      // 문서 권장 순서: 경매 정보 조회 먼저, 그 다음 경매방 참여
      debugLog('1️⃣ 경매 정보 조회 먼저 시도 (자동)');
      auctionSocketService.requestAuctionInfo(auctionIndex);
    } else if (connected && auctionIndex && !autoRequestData) {
      debugLog('🏛️ 연결됨 - 자동 데이터 요청은 비활성화됨:', auctionIndex);
    }
  }, [auctionIndex, autoRequestData, debugLog]);

  const handleAuctionInfoReceived = useCallback((response: ResponseModel<AuctionDetailView>) => {
    debugLog('🏛️ [useAuctionSocket] 경매 정보 처리:', response);
    
    // 서버 응답 형식: statusCode: 200, message: "success"
    if (response.statusCode === 200 && response.message === 'success' && response.data) {
      // lot 데이터에 초기값 설정
      const processedData = {
        ...response.data,
        lots: response.data.lots.map(lot => ({
          ...lot,
          currentPrice: lot.currentPrice || lot.startPrice,
          bidCount: lot.bidCount || 0,
          highestBidder: lot.highestBidder || undefined
        }))
      };
      
      setAuctionData(processedData);
      setError(null);
      debugLog('✅ 경매 데이터 업데이트 완료:', JSON.stringify(processedData, null, 2));
      
      // 경매 정보를 성공적으로 받았으면 경매방에 참여
      if (auctionIndex) {
        debugLog('2️⃣ 경매 정보 수신 성공 -> 경매방 참여 시도');
        setTimeout(() => {
          auctionSocketService.joinAuction(auctionIndex);
        }, 300);
      }
    } else {
      devError('❌ 경매 정보 오류:', response.error);
      setError(response.error?.message || response.message || '경매 정보를 가져오는데 실패했습니다.');
    }
  }, [auctionIndex, debugLog]);

  const handleAuctionNotFound = useCallback((errorResponse: ResponseModel<any>) => {
    devError('❌ 경매를 찾을 수 없음:', errorResponse);
    setError('경매를 찾을 수 없습니다.');
    setAuctionData(null);
  }, []);

  const handleAuctionError = useCallback((errorResponse: ResponseModel<any>) => {
    devError('❌ [useAuctionSocket] 경매 에러 상세:', {
      전체응답: errorResponse,
      에러메시지: errorResponse.error?.message || errorResponse.message,
      커스텀메시지: errorResponse.error?.customMessage,
      에러코드: errorResponse.error?.code,
      상태코드: errorResponse.statusCode,
      데이터: errorResponse.data,
      현재페이지: window.location.pathname,
      타입: typeof errorResponse,
      스택트레이스: new Error().stack
    });
    
    // 서버 내부 에러 (ERR_INVALID_ARG_TYPE)는 사용자에게 친화적인 메시지로 변환
    const isServerInternalError = errorResponse.error?.code === 'ERR_INVALID_ARG_TYPE' ||
                                 errorResponse.statusCode === 500 ||
                                 errorResponse.message?.includes('Buffer') ||
                                 errorResponse.message?.includes('ArrayBuffer') ||
                                 errorResponse.message?.includes('Received undefined');
    
    let errorMessage = '경매 처리 중 오류가 발생했습니다.';
    
    // customMessage가 있으면 우선적으로 사용
    if (errorResponse.error?.customMessage) {
      errorMessage = errorResponse.error.customMessage;
      devLog('✅ [useAuctionSocket] customMessage 사용:', errorMessage);
    } else if (isServerInternalError) {
      // 서버 내부 에러는 사용자 친화적 메시지로 변환
      devLog('🔧 [useAuctionSocket] 서버 내부 에러 감지 - 사용자 친화적 메시지로 변환');
      errorMessage = '서버에서 일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
    } else if (errorResponse.error?.message) {
      errorMessage = errorResponse.error.message;
    } else if (errorResponse.message) {
      errorMessage = errorResponse.message;
    } else if (errorResponse.error?.code) {
      errorMessage = `에러 코드: ${errorResponse.error.code}`;
    }
    
    devError(`❌ [useAuctionSocket] 사용자에게 표시할 에러 메시지: "${errorMessage}"`);
    setError(errorMessage);
  }, []);

  const handleBidPlaced = useCallback((bidData: any) => {
    devLog('💰 [useAuctionSocket] 실시간 입찰 데이터 수신:', bidData);
    devLog('💰 [useAuctionSocket] 입찰 데이터 타입:', typeof bidData);
    devLog('💰 [useAuctionSocket] 입찰 데이터 구조:', JSON.stringify(bidData, null, 2));
    
    // BidPlacedData 형태 검증 (실시간 입찰 데이터) - 명세 기반
    const isValidBidData = (data: any): data is BidPlacedData => {
      if (!data || typeof data !== 'object') {
        devLog('❌ 데이터가 객체가 아님:', typeof data);
        return false;
      }
      
      // 필수 필드들 (현장 입찰자의 경우 user 정보가 없을 수 있음)
      const requiredFields = ['lotIndex', 'price', 'code']; // 최소 필수 필드만
      
      const hasRequiredFields = requiredFields.every(field => {
        const hasField = field in data && data[field] !== undefined && data[field] !== null;
        devLog(`🔍 필드 "${field}" 존재:`, hasField, '값:', data?.[field]);
        return hasField;
      });
      
      devLog('🔍 실시간 입찰 데이터 유효성 검사:', {
        데이터타입: typeof data,
        필수필드존재: hasRequiredFields,
        lotIndex: data?.lotIndex,
        price: data?.price,
        usdPrice: data?.usdPrice,
        userIndex: data?.userIndex,
        userName: data?.userName,
        code: data?.code,
        전체구조: Object.keys(data || {}),
        현장입찰여부: !data?.userIndex, // userIndex가 없으면 현장 입찰로 간주
        CMS입찰여부: data?.code && typeof data.code === 'string' && data.code.startsWith && (data.code.startsWith('F') || /^\d+$/.test(data.code))
      });
      
      return hasRequiredFields;
    };
    
    // 유효한 실시간 입찰 데이터만 처리
    if (isValidBidData(bidData)) {
      devLog('✅ [useAuctionSocket] 유효한 실시간 입찰 데이터 처리:', bidData);
      
      // BidPlacedData를 BidHistoryItem 형태로 변환 (명세 기반)
      const bidHistoryItem: BidHistoryItem = {
        _id: `realtime-${Date.now()}-${bidData.code}`, // 실시간 입찰은 임시 ID (중복 방지를 위해 code 포함)
        user: bidData.userIndex ? {
          // 사용자 입찰인 경우 user 정보 포함
          index: bidData.userIndex,
          name: bidData.userName || null,
          cellphone: bidData.userCellphone || null,
          email: bidData.userEmail || null,
          locationType: 'DOMESTIC' // 기본값
        } : null, // 현장 입찰인 경우 user는 null
        auction: auctionIndex || 0,
        lot: bidData.lotIndex, // lotIndex를 lot 필드에 매핑
        price: bidData.price,
        exchangeRate: bidData.usdPrice ? (bidData.price / bidData.usdPrice) : 1,
        isWon: false,
        code: bidData.code,
        createAt: new Date().toISOString(),
        __v: 0
      };
      
      // 기존 입찰 내역에 새로운 입찰을 맨 앞에 추가 (실시간 누적)
      setBidHistory(prev => {
        // 중복 방지 로직 개선 - 현장 입찰과 사용자 입찰 모두 고려
        const isDuplicate = prev.some(existingBid => {
          // 같은 코드, 같은 가격, 같은 lot의 입찰은 중복으로 간주 (5초 이내)
          const sameCode = existingBid.code === bidHistoryItem.code;
          const samePrice = existingBid.price === bidHistoryItem.price;
          const sameLot = existingBid.lot === bidHistoryItem.lot;
          const timeWithin5Seconds = Math.abs(
            new Date(existingBid.createAt).getTime() - 
            new Date(bidHistoryItem.createAt).getTime()
          ) < 5000; // 5초 이내
          
          // 현장 입찰(user가 없는 경우)은 code로만 중복 체크
          if (!existingBid.user && !bidHistoryItem.user) {
            return sameCode && samePrice && sameLot && timeWithin5Seconds;
          }
          
          // 사용자 입찰(user가 있는 경우)은 userIndex로도 중복 체크
          if (existingBid.user?.index && bidHistoryItem.user?.index) {
            const sameUser = existingBid.user.index === bidHistoryItem.user.index;
            return sameUser && samePrice && sameLot && timeWithin5Seconds;
          }
          
          // 한쪽은 현장 입찰, 한쪽은 사용자 입찰인 경우 code로 체크
          return sameCode && samePrice && sameLot && timeWithin5Seconds;
        });
        
        if (isDuplicate) {
          devLog('🔄 [useAuctionSocket] 중복 입찰 데이터 무시:', bidHistoryItem);
          return prev;
        }
        
        const newHistory = [bidHistoryItem, ...prev].sort((a, b) => b.price - a.price);
        devLog('📊 [useAuctionSocket] 실시간 입찰 내역 업데이트 - 총', newHistory.length, '개');
        devLog('📊 [useAuctionSocket] 새로 추가된 입찰:', {
          ...bidHistoryItem,
          입찰타입: bidHistoryItem.user ? '사용자입찰' : 'CMS현장입찰',
          입찰자정보: bidHistoryItem.user ? `${bidHistoryItem.user.name} (${bidHistoryItem.user.index})` : `현장입찰자 (${bidHistoryItem.code})`
        });
        return newHistory;
      }); 
      
      // 경매 데이터의 현재 가격 업데이트
      setAuctionData(prev => {
        if (!prev) return prev;
        
        const updatedLots = prev.lots.map(lot => {
          if (lot.index === bidData.lotIndex) {
            devLog(`🏷️ LOT ${lot.index} 가격 업데이트: ${lot.currentPrice || lot.startPrice} → ${bidData.price}`);
            return {
              ...lot,
              currentPrice: bidData.price,
              bidCount: (lot.bidCount || 0) + 1,
              highestBidder: {
                userIndex: bidData.userIndex || null, // 사전입찰자는 userIndex가 없을 수 있음
                code: bidData.code
              }
            };
          }
          return lot;
        });

        const updatedData = {
          ...prev,
          lots: updatedLots,
          currentPrice: bidData.price // 전체 경매의 현재 가격도 업데이트
        };
        
        devLog('🏛️ 경매 데이터 업데이트 완료 (실시간)');
        return updatedData;
      });
    } else {
      devWarn('❌ [useAuctionSocket] 무효한 실시간 입찰 데이터 무시:', bidData);
      devWarn('❌ 예상 형태: { lotIndex: number, price: number, code: string, ... }');
    }
  }, [auctionIndex]);

  const handleMaxReconnectAttemptsReached = useCallback(() => {
    setError('서버 연결에 실패했습니다. 페이지를 새로고침해주세요.');
  }, []);

  // 인증 에러 핸들러 (토큰 만료/무효 시)
  const handleAuthError = useCallback((errorResponse: any) => {
    devError('🚫 [useAuctionSocket] 인증 에러:', errorResponse);
    
    // customMessage가 있으면 우선적으로 사용
    let authErrorMessage = '인증 오류가 발생했습니다.';
    if (errorResponse?.customMessage) {
      authErrorMessage = errorResponse.customMessage;
      devLog('✅ [useAuctionSocket] auth-error customMessage 사용:', authErrorMessage);
    } else if (errorResponse?.message) {
      authErrorMessage = errorResponse.message;
    }
    
    // 토큰이 유효하지 않아서 발생한 에러인지 확인
    const isTokenInvalid = errorResponse?.code === 'AUTH_REQUIRED' || 
                          errorResponse?.code === 'AUTH_INVALID' || 
                          errorResponse?.code === 'AUTH_EXPIRED' ||
                          errorResponse?.message?.includes('인증') ||
                          errorResponse?.message?.includes('토큰') ||
                          errorResponse?.message?.includes('로그인');
    
    if (isTokenInvalid) {
      // 현재 페이지 확인
      const currentPath = window.location.pathname;
      const isLiveBidPage = currentPath.includes('/livebid') || currentPath.includes('/live-bid');
      const isLiveBidRequestPage = currentPath.includes('/live-bid-request');
      
      // LiveBidRequest 전용 처리: 별도 이벤트로 페이지 내부에서 로그아웃/리다이렉트
      if (isLiveBidRequestPage) {
        devLog('� [useAuctionSocket] LiveBidRequest 페이지에서 인증 에러 - 로그인 페이지로 리다이렉트');
        // LiveBidRequest 페이지에서는 바로 로그인 페이지로 리다이렉트
        window.dispatchEvent(new CustomEvent('live-bid-request-auth-error', {
          detail: {
            error: errorResponse,
            timestamp: Date.now(),
            reason: 'token_invalid',
            customMessage: authErrorMessage
          }
        }));
        return;
      }
      
      // LiveBid 포함 나머지 페이지: 전역 세션 만료 처리 → 모달 확인 시 로그아웃
      devLog('�🔑 [useAuctionSocket] 토큰 무효로 인한 인증 에러 감지 - 세션 만료 처리');

      // 에러 메시지 설정 (토큰 만료 상황 명시)
      setError('로그인이 만료되었습니다.');

      // 전역 세션 만료 처리 (UserAuthContext에서 모달 표시 후 확인 시 logout 수행)
      notifySessionExpired();
    } else {
      // 일반적인 에러로 처리
      setError(authErrorMessage);
    }
  }, []);

  // 관리자 이벤트 핸들러들
  const handleBidStarted = useCallback((data: any) => {
    devLog('🏁 [useAuctionSocket] 작품 경매 시작:', data);
    
    // bid-started 이벤트로 현재 진행 중인 lot 설정
    const resolvedLotNo = data?.lotNo ?? data?.no;
    const resolvedLotIndex = data?.lotIndex ?? data?.index;

    if (!data || resolvedLotNo == null || resolvedLotIndex == null) {
      devWarn('⚠️ [useAuctionSocket] bid-started 이벤트에 필요한 필드가 부족함:', {
        hasData: !!data,
        resolvedAuctionIndex: data?.auctionIndex ?? auctionIndex,
        resolvedLotNo,
        resolvedLotIndex,
        raw: data
      });
      return;
    }

    const newCurrentLot = {
      lotIndex: toSafeNumber(resolvedLotIndex, 0),
      lotNo: toSafeNumber(resolvedLotNo, 0),
      lotName: data.lotName || data.name || ''
    };

    devLog('📌 [useAuctionSocket] 현재 진행 중인 lot 설정 (bid-started):', newCurrentLot);
    setCurrentLot(newCurrentLot);
    
    // 경매 종료 상태 해제
    setIsAuctionEnded(false);
  }, [auctionIndex, toSafeNumber]);

  const handleNextLot = useCallback((data: any) => {
    devLog('⏭️ [useAuctionSocket] 다음 작품 이벤트 수신:', data);
    
    // next-lot 이벤트로 현재 진행 중인 lot 업데이트
    if (data) {
      const resolvedLotIndex = data.lotIndex ?? data.index;
      if (resolvedLotIndex == null) {
        devWarn('⚠️ [useAuctionSocket] next-lot 이벤트에 lotIndex/index가 없음:', data);
        return;
      }

      // 데이터 구조에 따라 다음 lot 정보 설정
      const nextLotData = {
        lotIndex: toSafeNumber(resolvedLotIndex, 0),
        lotNo: toSafeNumber(data.lotNo ?? data.no, 0),
        lotName: data.lotName || data.name || ''
      };
      
      devLog('🔄 [useAuctionSocket] 현재 진행 중인 lot 업데이트 (next-lot):', nextLotData);
      setCurrentLot(nextLotData);
      
      // 경매 종료 상태 해제
      setIsAuctionEnded(false);
    }
    
    devLog('🔄 [useAuctionSocket] 서버에서 lot 변경 완료 - 경매 정보 재요청');
    
    // 서버에서 next-lot 처리가 완료되었으므로 최신 경매 정보를 요청
    const resolvedAuctionIndex = data?.auctionIndex ?? auctionIndex;
    if (resolvedAuctionIndex != null) {
      devLog('🏛️ [useAuctionSocket] next-lot 이벤트로 인한 경매 정보 재요청:', resolvedAuctionIndex);
      
      // 입찰 내역도 클리어 (새로운 lot으로 전환)
      devLog('🧹 [useAuctionSocket] 새 lot으로 전환 - 입찰 내역 클리어');
      setBidHistory([]);
      
      // 현재 auction 데이터 로깅
      devLog('📊 [useAuctionSocket] 현재 auctionData 상태:', {
        hasData: !!auctionData,
        lotsCount: auctionData?.lots?.length || 0,
        lotStates: auctionData?.lots?.map(lot => ({
          index: lot.index,
          lotNo: lot.lotNo,
          ended: lot.ended,
          disabled: lot.disabled
        })) || []
      });
      
      // 서버 처리 완료 후 최신 데이터 요청 - 즉시 요청으로 변경
      devLog('🔄 [useAuctionSocket] next-lot 이후 즉시 경매 정보 재요청 실행');
      auctionSocketService.requestAuctionInfo(resolvedAuctionIndex);
      
      // 추가로 0.3초 후 한 번 더 요청하여 확실히 최신 데이터 획득
      setTimeout(() => {
        devLog('🔄 [useAuctionSocket] next-lot 이후 추가 경매 정보 재요청');
        auctionSocketService.requestAuctionInfo(resolvedAuctionIndex);
      }, 300);
    } else {
      devWarn('⚠️ [useAuctionSocket] next-lot 이벤트에서 auctionIndex가 없음:', data);
    }
  }, [auctionData, auctionIndex, toSafeNumber]);

  // 경매 종료 이벤트 핸들러
  const handleAuctionEnded = useCallback(() => {
    devLog('🏁 [useAuctionSocket] 경매 종료 이벤트 수신');
    setIsAuctionEnded(true);
    setCurrentLot(null);
  }, []);

  const handleBanUser = useCallback((data: any) => {
    devLog('🚫 [useAuctionSocket] 사용자 차단:', data);
    setIsBanned(true);
  }, []);

  const handleUnbanUser = useCallback((data: any) => {
    devLog('✅ [useAuctionSocket] 사용자 차단 해제:', data);
    setIsBanned(false);
  }, []);

  // 카운트다운 관련 핸들러들
  const handleStartCountdown = useCallback((data: any) => {
    devLog('⏰ [useAuctionSocket] 카운트다운 시작:', data);
    setCountdownStatus({
      isRunning: true,
      isPaused: false,
      remainingTime: data.duration || 10,
      lotIndex: toOptionalSafeNumber(data?.lotIndex ?? data?.index)
    });
  }, [toOptionalSafeNumber]);

  const handleCountdownTick = useCallback((data: any) => {
    devLog('⏱️ [useAuctionSocket] 카운트다운 틱:', data);
    setCountdownStatus(prev => ({
      ...prev,
      remainingTime: data.remainingTime,
      lotIndex: toOptionalSafeNumber(data?.lotIndex ?? data?.index) ?? prev.lotIndex
    }));
  }, [toOptionalSafeNumber]);

  const handleCountdownEnded = useCallback((data: any) => {
    devLog('⏰ [useAuctionSocket] 카운트다운 종료:', data);
    setCountdownStatus({
      isRunning: false,
      isPaused: false,
      remainingTime: 0,
      lotIndex: data.lotIndex
    });
  }, []);

  const handlePauseCountdown = useCallback((data: any) => {
    devLog('⏸️ [useAuctionSocket] 카운트다운 일시정지:', data);
    setCountdownStatus(prev => ({
      ...prev,
      isPaused: true
    }));
  }, []);

  const handleResumeCountdown = useCallback((data: any) => {
    devLog('▶️ [useAuctionSocket] 카운트다운 재개:', data);
    setCountdownStatus(prev => ({
      ...prev,
      isPaused: false
    }));
  }, []);

  const handleStopCountdown = useCallback((data: any) => {
    devLog('⏹️ [useAuctionSocket] 카운트다운 중지:', data);
    setCountdownStatus({
      isRunning: false,
      isPaused: false,
      remainingTime: 0,
      lotIndex: data.lotIndex
    });
  }, []);

  const handleTimerControlResponse = useCallback((response: any) => {
    devLog('🎮 [useAuctionSocket] 타이머 제어 응답:', response);
    if (response.timerStatus) {
      setCountdownStatus({
        isRunning: response.timerStatus.isRunning,
        isPaused: response.timerStatus.isPaused,
        remainingTime: response.timerStatus.remainingTime,
        lotIndex: response.lotIndex
      });
    }
  }, []);

  const handleTimerStatusResponse = useCallback((response: any) => {
    devLog('📊 [useAuctionSocket] 타이머 상태 응답:', response);
    if (response.timerStatus) {
      setCountdownStatus({
        isRunning: response.timerStatus.isRunning,
        isPaused: response.timerStatus.isPaused,
        remainingTime: response.timerStatus.remainingTime,
        lotIndex: response.lotIndex
      });
    }
  }, []);

  // 입찰 내역 핸들러 (bid-history 이벤트용 - 초기 데이터 로딩)
  const handleBidHistory = useCallback((bidHistoryData: BidHistoryItem[]) => {
    devLog('📚 [useAuctionSocket] bid-history 초기 데이터 수신:', bidHistoryData);
    devLog('📚 [useAuctionSocket] 받은 초기 입찰 내역 개수:', bidHistoryData.length);
    
    if (bidHistoryData.length === 0) {
      devLog('📚 [useAuctionSocket] 초기 입찰 내역이 없습니다 (빈 배열)');
      setBidHistory([]);
      return;
    }
    
    // 가격 순으로 정렬 (높은 가격부터) - 초기 데이터 설정
    const sortedHistory = [...bidHistoryData].sort((a, b) => b.price - a.price);
    
    setBidHistory(sortedHistory);
    devLog('📚 [useAuctionSocket] 초기 입찰 내역 설정 완료 - BidHistoryItem[]:', sortedHistory.length, '개');
    devLog('📚 [useAuctionSocket] 최고가 입찰:', sortedHistory[0]);
    
    // 경매 데이터의 현재 가격도 최고가로 업데이트
    if (sortedHistory.length > 0) {
      const highestBid = sortedHistory[0];
      setAuctionData(prev => {
        if (!prev) return prev;
        
        const updatedLots = prev.lots.map(lot => {
          if (lot.index === highestBid.lot) {
            devLog(`🏷️ LOT ${lot.index} 초기 현재가 설정: ${highestBid.price}`);
            return {
              ...lot,
              currentPrice: highestBid.price,
              bidCount: sortedHistory.filter(bid => bid.lot === lot.index).length,
              highestBidder: {
                userIndex: highestBid.user?.index || null, // 사전입찰자는 user가 없을 수 있음
                code: highestBid.code
              }
            };
          }
          return lot;
        });

        return {
          ...prev,
          lots: updatedLots,
          currentPrice: highestBid.price
        };
      });
    }
  }, []);

  // 액션 함수들
  const connectSocket = useCallback(() => {
    auctionSocketService.connect();
  }, []);

  const disconnectSocket = useCallback(() => {
    auctionSocketService.disconnect();
  }, []);

  const getAuctionInfo = useCallback((auctionIndex: number) => {
    auctionSocketService.requestAuctionInfo(auctionIndex);
  }, []);

  const joinAuction = useCallback((auctionIndex: number) => {
    setIsBanned(false); // 경매방 참여 시 차단 상태 초기화
    auctionSocketService.joinAuction(auctionIndex);
  }, []);

  const placeBid = useCallback((auctionIndex: number, lotIndex: number, price: number) => {
    devLog('🎯 [useAuctionSocket] placeBid 호출:', { auctionIndex, lotIndex, price });
    auctionSocketService.placeBid(auctionIndex, lotIndex, price);
  }, []);

  const getBidHistory = useCallback((auctionIndex: number, lotIndex: number) => {
    auctionSocketService.getBidHistory(auctionIndex, lotIndex);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearBidHistory = useCallback(() => {
    setBidHistory([]);
  }, []);

  // 관리자 전용 기능들
  const startBid = useCallback((auctionIndex: number, lotNo: number) => {
    devLog('🏁 [useAuctionSocket] startBid 호출:', { auctionIndex, lotNo });
    auctionSocketService.startBid(auctionIndex, lotNo);
  }, []);

  const cmsPlaceBid = useCallback((auctionIndex: number, lotIndex: number, price: number, code: string) => {
    devLog('🏢 [useAuctionSocket] cmsPlaceBid 호출:', { auctionIndex, lotIndex, price, code });
    auctionSocketService.cmsPlaceBid(auctionIndex, lotIndex, price, code);
  }, []);

  const banUser = useCallback((auctionIndex: number, userIndex: number) => {
    devLog('🚫 [useAuctionSocket] banUser 호출:', { auctionIndex, userIndex });
    auctionSocketService.banUser(auctionIndex, userIndex);
  }, []);

  const unbanUser = useCallback((auctionIndex: number, userIndex: number) => {
    devLog('✅ [useAuctionSocket] unbanUser 호출:', { auctionIndex, userIndex });
    auctionSocketService.unbanUser(auctionIndex, userIndex);
  }, []);

  const getBanUserList = useCallback((auctionIndex: number) => {
    devLog('📋 [useAuctionSocket] getBanUserList 호출:', { auctionIndex });
    auctionSocketService.getBanUserList(auctionIndex);
  }, []);

  const nextLot = useCallback((auctionIndex: number) => {
    devLog('⏭️ [useAuctionSocket] nextLot 호출:', { auctionIndex });
    auctionSocketService.nextLot(auctionIndex);
  }, []);

  // 10초 카운트다운 시작 (간단한 버전 - 호환성 유지)
  const auctionCountdown = useCallback(() => {
    devLog('⏰ [useAuctionSocket] auctionCountdown 호출');
    auctionSocketService.auctionCountdown();
  }, []);

  // 새로운 타이머 제어 함수들
  const timerControl = useCallback((auctionIndex: number, lotIndex: number, action: 'start' | 'pause' | 'resume' | 'reset' | 'stop', duration?: number) => {
    devLog('🎮 [useAuctionSocket] timerControl 호출:', { auctionIndex, lotIndex, action, duration });
    auctionSocketService.timerControl(auctionIndex, lotIndex, action, duration);
  }, []);

  const getTimerStatus = useCallback((auctionIndex: number, lotIndex?: number) => {
    devLog('📊 [useAuctionSocket] getTimerStatus 호출:', { auctionIndex, lotIndex });
    auctionSocketService.getTimerStatus(auctionIndex, lotIndex);
  }, []);

  // 컴포넌트 마운트 시 이벤트 리스너 등록
  useEffect(() => {
    // 이벤트 리스너 등록
    auctionSocketService.on('connected', handleConnectionChange);
    auctionSocketService.on('auction-info-received', handleAuctionInfoReceived);
    auctionSocketService.on('auction-not-found', handleAuctionNotFound);
    auctionSocketService.on('auction-error', handleAuctionError);
    auctionSocketService.on('bid-placed', handleBidPlaced);
    auctionSocketService.on('bid-history', handleBidHistory);
    auctionSocketService.on('max-reconnect-attempts-reached', handleMaxReconnectAttemptsReached);
    auctionSocketService.on('auth-error', handleAuthError); // 인증 에러 리스너 추가
    
    // 관리자 이벤트 리스너들
    auctionSocketService.on('bid-started', handleBidStarted);
    auctionSocketService.on('next-lot', handleNextLot);
    auctionSocketService.on('auction-ended', handleAuctionEnded);
    auctionSocketService.on('ban-user', handleBanUser);
    auctionSocketService.on('unban-user', handleUnbanUser);

    // 카운트다운 관련 이벤트 리스너들
    auctionSocketService.on('start-countdown', handleStartCountdown);
    auctionSocketService.on('countdown-tick', handleCountdownTick);
    auctionSocketService.on('countdown-ended', handleCountdownEnded);
    auctionSocketService.on('pause-countdown', handlePauseCountdown);
    auctionSocketService.on('resume-countdown', handleResumeCountdown);
    auctionSocketService.on('stop-countdown', handleStopCountdown);
    auctionSocketService.on('timer-control-response', handleTimerControlResponse);
    auctionSocketService.on('timer-status-response', handleTimerStatusResponse);

    // 클린업
    return () => {
      auctionSocketService.off('connected', handleConnectionChange);
      auctionSocketService.off('auction-info-received', handleAuctionInfoReceived);
      auctionSocketService.off('auction-not-found', handleAuctionNotFound);
      auctionSocketService.off('auction-error', handleAuctionError);
      auctionSocketService.off('bid-placed', handleBidPlaced);
      auctionSocketService.off('bid-history', handleBidHistory);
      auctionSocketService.off('max-reconnect-attempts-reached', handleMaxReconnectAttemptsReached);
      auctionSocketService.off('auth-error', handleAuthError); // 인증 에러 리스너 정리
      
      // 관리자 이벤트 리스너 정리
      auctionSocketService.off('bid-started', handleBidStarted);
      auctionSocketService.off('next-lot', handleNextLot);
      auctionSocketService.off('auction-ended', handleAuctionEnded);
      auctionSocketService.off('ban-user', handleBanUser);
      auctionSocketService.off('unban-user', handleUnbanUser);

      // 카운트다운 관련 이벤트 리스너 정리
      auctionSocketService.off('start-countdown', handleStartCountdown);
      auctionSocketService.off('countdown-tick', handleCountdownTick);
      auctionSocketService.off('countdown-ended', handleCountdownEnded);
      auctionSocketService.off('pause-countdown', handlePauseCountdown);
      auctionSocketService.off('resume-countdown', handleResumeCountdown);
      auctionSocketService.off('stop-countdown', handleStopCountdown);
      auctionSocketService.off('timer-control-response', handleTimerControlResponse);
      auctionSocketService.off('timer-status-response', handleTimerStatusResponse);
    };
  }, [
    handleConnectionChange,
    handleAuctionInfoReceived,
    handleAuctionNotFound,
    handleAuctionError,
    handleBidPlaced,
    handleBidHistory,
    handleMaxReconnectAttemptsReached,
    handleAuthError, // 인증 에러 핸들러 추가
    handleBidStarted,
    handleNextLot,
    handleAuctionEnded,
    handleBanUser,
    handleUnbanUser,
    handleStartCountdown,
    handleCountdownTick,
    handleCountdownEnded,
    handlePauseCountdown,
    handleResumeCountdown,
    handleStopCountdown,
    handleTimerControlResponse,
    handleTimerStatusResponse
  ]);

  // 토큰 변경 시 소켓 서비스에 토큰 설정
  useEffect(() => {
    const tokenType = useAdminToken ? 'admin' : 'user';
    devLog('🔑 [useAuctionSocket] 토큰 설정:', {
      hasToken: !!accessToken,
      tokenType,
      tokenLength: accessToken?.length || 0
    });
    auctionSocketService.setToken(accessToken, tokenType);
  }, [accessToken, useAdminToken]);

  // 자동 연결 설정에 따른 연결 처리 (로그인 여부와 상관없이)
  useEffect(() => {
    const baseUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3000';
    const fullSocketUrl = `${baseUrl}/auction`;
    
    devLog('🔐 [useAuctionSocket] 소켓 연결 상태 확인:', { 
      autoConnect, 
      hasToken: !!accessToken,
      socketUrl: fullSocketUrl
    });
    
    if (autoConnect) {
      devLog('🚀 [useAuctionSocket] 소켓 연결 시작 (로그인 여부와 관계없이 게스트 연결 지원)');
      devLog('🌐 [useAuctionSocket] 연결할 엔드포인트:', fullSocketUrl);
      auctionSocketService.connect();
    }
  }, [autoConnect, accessToken]);

  // 디버그 모드 자동 소켓 닫기 비활성화 - 개발 편의성을 위해 제거
  // useEffect(() => {
  //   let warningTimer: NodeJS.Timeout | null = null;
  //   let autoCloseTimer: NodeJS.Timeout | null = null;

  //   if (isDebugMode && isConnected) {
  //     devLog('🚧 [useAuctionSocket] 디버그 모드 - 1분 후 자동 소켓 닫기 타이머 시작');
      
  //     // 30초 후 경고 메시지
  //     warningTimer = setTimeout(() => {
  //       devLog('🚧 [useAuctionSocket] 디버그 모드 - 30초 후 소켓이 자동으로 닫힙니다');
  //       setError('디버그 모드: 30초 후 소켓 연결이 자동으로 해제됩니다. 비용 절약을 위한 조치입니다.');
  //     }, 30000); // 30초

  //     // 1분 후 자동 닫기
  //     autoCloseTimer = setTimeout(() => {
  //       devLog('🚧 [useAuctionSocket] 디버그 모드 - 1분 경과, 소켓 자동 닫기');
  //       auctionSocketService.disconnect();
  //       setError('디버그 모드에서 1분이 경과하여 소켓 연결이 자동으로 해제되었습니다. 새로고침하여 다시 연결하세요.');
  //     }, 60000); // 1분 (60초)
  //   }

  //   return () => {
  //     if (warningTimer) {
  //       clearTimeout(warningTimer);
  //     }
  //     if (autoCloseTimer) {
  //       clearTimeout(autoCloseTimer);
  //     }
  //   };
  // }, [isDebugMode, isConnected]);

  // 컴포넌트 언마운트 시 연결 해제
  useEffect(() => {
    return () => {
      if (isConnected) {
        auctionSocketService.disconnect();
      }
    };
  }, [isConnected]);

  return {
    isConnected,
    auctionData,
    bidHistory,
    countdownStatus,
    currentLot,
    isAuctionEnded,
    isBanned,
    error,
    connectSocket,
    disconnectSocket,
    getAuctionInfo,
    joinAuction,
    placeBid,
    getBidHistory,
    startBid,
    cmsPlaceBid,
    banUser,
    unbanUser,
    getBanUserList,
    nextLot,
    timerControl,
    getTimerStatus,
    auctionCountdown,
    clearError,
    clearBidHistory
  };
}
